import { useState, useEffect, useRef } from 'react'

import { useI18n } from '@/lib/i18n'
import type { EventConfig, AudioConfig } from '@/types'
import { Mic, Speaker, Play, Square, AlertCircle } from 'lucide-react'

interface AudioSettingsProps {
  event: EventConfig
  onChange: (updates: Partial<EventConfig>) => void
}

export default function AudioSettings({ event, onChange }: AudioSettingsProps) {
  const { t } = useI18n()
  const [inputs, setInputs] = useState<MediaDeviceInfo[]>([])
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([])
  const [isTesting, setIsTesting] = useState(false)
  const [level, setLevel] = useState(0)
  const [error, setError] = useState<string | null>(null)
  
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const testGeneration = useRef(0)

  const audioConfig: AudioConfig = event.audio || {
    microphoneDeviceId: '',
    outputDeviceId: '',
    microphoneGain: 1.0,
    enabled: true,
  }

  useEffect(() => {
    async function getDevices() {
      try {
        // Request permission first to get device labels
        await navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
          stream.getTracks().forEach(t => t.stop())
        }).catch(() => {})

        const devices = await navigator.mediaDevices.enumerateDevices()
        setInputs(devices.filter(d => d.kind === 'audioinput'))
        setOutputs(devices.filter(d => d.kind === 'audiooutput'))
      } catch (err) {
        setError('Failed to list audio devices')
      }
    }
    getDevices()
  }, [])

  const updateAudioConfig = (updates: Partial<AudioConfig>) => {
    onChange({
      audio: {
        ...audioConfig,
        ...updates,
      },
    })
  }

  const startTest = async () => {
    const generation = ++testGeneration.current
    try {
      setError(null)
      setIsTesting(true)
      
      const constraints: MediaStreamConstraints = {
        audio: audioConfig.microphoneDeviceId ? { deviceId: { exact: audioConfig.microphoneDeviceId } } : true,
      }
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      if (generation !== testGeneration.current) {
        stream.getTracks().forEach(track => track.stop())
        return
      }
      streamRef.current = stream

      const audioContext = new AudioContext()
      audioContextRef.current = audioContext

      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 256
      
      const gainNode = audioContext.createGain()
      gainNode.gain.value = audioConfig.microphoneGain

      source.connect(gainNode)
      gainNode.connect(analyser)
      // Don't connect to destination to avoid feedback loop!

      analyserRef.current = analyser

      const dataArray = new Uint8Array(analyser.frequencyBinCount)
      
      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray)
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length
        setLevel(average / 128) // Normalize to 0-1 approx
        animationFrameRef.current = requestAnimationFrame(updateLevel)
      }
      
      updateLevel()
    } catch (err) {
      if (generation !== testGeneration.current) return
      setError(err instanceof Error ? err.message : 'Failed to start audio test')
      setIsTesting(false)
    }
  }

  const stopTest = () => {
    testGeneration.current++
    setIsTesting(false)
    setLevel(0)
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop())
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') void audioContextRef.current.close()
    streamRef.current = null
    audioContextRef.current = null
    animationFrameRef.current = null
  }

  useEffect(() => {
    return () => stopTest()
  }, [])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Audio Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Configure microphone recording and audio output for the event.</p>
      </div>

      <p className="text-xs text-muted-foreground">{t('inheritance.deviceHint')}</p>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Input Settings */}
        <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-semibold">
              <Mic className="h-4 w-4 text-primary" />
              Microphone Input
            </h3>
            <label className="relative inline-flex cursor-pointer">
              <input 
                type="checkbox" 
                aria-label="Microphone input"
                checked={audioConfig.enabled} 
                onChange={(e) => updateAudioConfig({ enabled: e.target.checked })} 
                className="opacity-0 absolute peer" 
              />
              <div className="w-9 h-5 bg-muted rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
            </label>
          </div>

          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Select Device</label>
              <select
                value={audioConfig.microphoneDeviceId || ''}
                onChange={(e) => updateAudioConfig({ microphoneDeviceId: e.target.value })}
                className="w-full mt-1 rounded-lg border border-input bg-background px-4 py-2 text-sm"
                disabled={!audioConfig.enabled}
              >
                <option value="">Default Microphone</option>
                {audioConfig.microphoneDeviceId && !inputs.some(d => d.deviceId === audioConfig.microphoneDeviceId) && <option value={audioConfig.microphoneDeviceId}>{t('inheritance.unavailableDevice')}</option>}
                {inputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0, 5)}`}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Input Gain</label>
                <span className="text-xs font-mono text-muted-foreground">{Math.round(audioConfig.microphoneGain * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={audioConfig.microphoneGain}
                onChange={(e) => updateAudioConfig({ microphoneGain: parseFloat(e.target.value) })}
                className="w-full mt-1"
                disabled={!audioConfig.enabled}
              />
            </div>

            {/* Live Meter */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Signal Level</label>
                {!isTesting ? (
                  <button
                    onClick={startTest}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                    disabled={!audioConfig.enabled}
                  >
                    <Play className="h-3 w-3" /> Test
                  </button>
                ) : (
                  <button
                    onClick={stopTest}
                    className="flex items-center gap-1 text-xs text-destructive hover:underline"
                  >
                    <Square className="h-3 w-3" /> Stop
                  </button>
                )}
              </div>
              <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 transition-all duration-100" 
                  style={{ width: `${Math.min(level * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Output Settings */}
        <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
          <h3 className="flex items-center gap-2 font-semibold">
            <Speaker className="h-4 w-4 text-primary" />
            Audio Output
          </h3>

          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Playback Device</label>
              <select
                value={audioConfig.outputDeviceId || ''}
                onChange={(e) => updateAudioConfig({ outputDeviceId: e.target.value })}
                className="w-full mt-1 rounded-lg border border-input bg-background px-4 py-2 text-sm"
              >
                <option value="">Default Speaker</option>
                {audioConfig.outputDeviceId && !outputs.some(d => d.deviceId === audioConfig.outputDeviceId) && <option value={audioConfig.outputDeviceId}>{t('inheritance.unavailableDevice')}</option>}
                {outputs.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>{d.label || `Speaker ${d.deviceId.slice(0, 5)}`}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Note: Speaker selection is only supported in some browsers (e.g. Chrome).</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sound Effects */}
      <div className="space-y-4 rounded-xl border border-border bg-card/50 p-5">
        <h3 className="flex items-center gap-2 font-semibold">
          <Speaker className="h-4 w-4 text-primary" />
          Sound Effects
        </h3>
        <div className="grid gap-4 md:grid-cols-2 pt-2">
          <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2.5">
            <div>
              <div className="text-sm font-medium">Countdown Sound</div>
              <div className="text-[11px] text-muted-foreground">Play a beep during countdown</div>
            </div>
            <label className="relative inline-flex cursor-pointer">
              <input 
                type="checkbox" 
                aria-label="Countdown sound"
                checked={audioConfig.counterSoundEnabled ?? true} 
                onChange={(e) => updateAudioConfig({ counterSoundEnabled: e.target.checked })} 
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-muted rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
            </label>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2.5">
            <div>
              <div className="text-sm font-medium">Shutter Sound</div>
              <div className="text-[11px] text-muted-foreground">Play a shutter sound when photo is taken</div>
            </div>
            <label className="relative inline-flex cursor-pointer">
              <input 
                type="checkbox" 
                aria-label="Shutter sound"
                checked={audioConfig.shutterSoundEnabled ?? true} 
                onChange={(e) => updateAudioConfig({ shutterSoundEnabled: e.target.checked })} 
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-muted rounded-full peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-transform peer-checked:after:translate-x-4" />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg border border-destructive/20">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  )
}
